import * as d3 from "d3";

import React, { useState, useEffect } from "react";

import { Container } from "react-bootstrap";
import { patientActions } from "../../actions/PatientActions";
import { useD3 } from "../../hooks/useD3";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

export default function TreeChart({
  jsonTree,
  primary,
  firstPos,
  names = [],
  seqIds = [],
  pruneSeqIds = [],
  constrainToSeqIds = false,
  noSeq = false,
  snpDistances = {},
  showFutureSequences = false,
}) {
  const dispatch = useDispatch();
  //const width = 600;
  const height = 500;
  const [width, setWidth] = useState(500);
  const outerRadius = height / 1.5;
  const innerRadius = outerRadius - 40;
  const tipLabelPadding = 10;

  const tooltip_y_offset = 150;
  const tooltip_x_offset = 100;

  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipId, setTooltipId] = useState("");
  const [tooltipName, setTooltipName] = useState("");
  const [tooltipSNPs, setTooltipSNPs] = useState("");
  const [tooltipDate, setTooltipDate] = useState("");
  const [tooltipDaysAgo, setTooltipDaysAgo] = useState("");
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const navigate = useNavigate();


  //NOTE: In this graph width = height and height = width
  const ref = useD3((svg) => {
    drawTree();
  }, [jsonTree, width, primary, names, seqIds, pruneSeqIds, constrainToSeqIds, noSeq, snpDistances, showFutureSequences]);

  const drawTree = () => {
    const highlightedSeqIds = new Set((seqIds || []).map((id) => String(id)));
    const pruneSeqIdSet = new Set(
      (pruneSeqIds || []).map((id) => String(id))
    );
    const shouldPrune = constrainToSeqIds && pruneSeqIdSet.size > 0;

    const getNodeSeqId = (node) => {
      const seqId = node?.data?.seq_id;
      if (seqId === null || seqId === undefined || seqId === "") {
        return null;
      }
      return String(seqId);
    };

    const getNodeMicroId = (node) => {
      const microId = node?.data?.micro_id;
      if (microId === null || microId === undefined || microId === "") {
        return null;
      }
      return microId;
    };

    const svg = d3.select(ref.current);
    svg.selectAll("*").remove(); // Clear previous content
  
    // Set dimensions
    svg.attr("width", width).attr("height", height);
    
    // D3 drawing logic below
      let square = 1;

      if (square) {
        svg
        .attr("viewBox", [-10, -10, height + 10, width + 25])
        .attr("font-family", "sans-serif")
        .attr("font-size", 10);
      } else {
        svg
        .attr("viewBox", [-outerRadius, -outerRadius, width, width])
        .attr("font-family", "sans-serif")
        .attr("font-size", 10);
      }

    //Tidies up the tree & filters out nodes that are after the first positive date
    //Ideally this should take place in the backend...
    function TidyUpTree(node, firstPosDate=null) {
      if (!node) return null;

      let nodeDate = node.collectdt ? new Date(node.collectdt) : null;
      // Recursively filter the children of the node
      if (node.branchset && node.branchset.length > 0) {
        node.branchset = node.branchset
            .map(child => TidyUpTree(child, firstPosDate))
            .filter(child => child !== null); // Remove null children after filtering
      } 

      // Remove inner nodes with distance 0 - promote children
      if (node.branchset && node.branchset.length > 0) {
        node.branchset = node.branchset.flatMap(child => {
            // If the child's length is zero and it has children, promote the grandchildren
            if (child.length === 0 && child.branchset && child.branchset.length > 0) {
                return child.branchset;
            } else {
                return [child];
            }
        });
    }
      // Filter inner nodes without children
      if (node.branchset && node.branchset.length === 0 && node.name && node.name.includes("Inner") && node.name.includes(": No data")) {
          return null;
      }

      // Consolidate inner nodes with only one child
      if (node.branchset && node.branchset.length === 1 && node.name && node.name.includes("Inner") && node.name.includes(": No data")) {
        // Aggregate the length from the current node to its child before bypassing
        if (node.length && node.length > 0) {
            node.branchset[0].length += node.length;
        }
        return node.branchset[0]; // Return child node directly
      }
      
      // Filter nodes based on their collection date if firstPosDate is provided
      if (firstPosDate && nodeDate && nodeDate > firstPosDate) {
          return null;
      }
  
      return node;
    }

    function pruneTreeBySeqIds(node, allowedSeqIds) {
      if (!node) return null;

      if (node.branchset && node.branchset.length > 0) {
        node.branchset = node.branchset
          .map((child) => pruneTreeBySeqIds(child, allowedSeqIds))
          .filter((child) => child !== null);
      }

      if (node.branchset && node.branchset.length > 0) {
        return node;
      }

      const nodeSeqId =
        node.seq_id !== null && node.seq_id !== undefined && node.seq_id !== ""
          ? String(node.seq_id)
          : null;
      if (nodeSeqId && allowedSeqIds.has(nodeSeqId)) {
        return node;
      }

      return null;
    }

    function calculateMinSNP(node) {
      let minSNP = Infinity;
  
      function traverse(node) {
          if (node.length && node.length > 0) {
              minSNP = Math.min(minSNP, node.length);
          }
  
          // Recursively check children if any
          if (node.branchset && node.branchset.length) {
              node.branchset.forEach(traverse);
          }
      }
      traverse(node);
      return isFinite(minSNP) ? minSNP : 0; // default to 0
    }
  
    // Convert firstPos to a Date object for comparison + 1 day buffer
    const firstPosDate = new Date(firstPos.split("T")[0]);
    firstPosDate.setDate(firstPosDate.getDate());
    
    var data = null;
    if (showFutureSequences) {
      data = TidyUpTree(jsonTree, null);
    } else {
      data = TidyUpTree(jsonTree, firstPosDate);
    }
    if (shouldPrune) {
      data = pruneTreeBySeqIds(data, pruneSeqIdSet);
    }
    if (!data) {
      return;
    }
    //const data = jsonTree;
    
    const min_snp = calculateMinSNP(data);
    data.min_snp = min_snp;

    const root = d3
      .hierarchy(data, (d) => d.branchset)
      .sum((d) => (d.branchset ? 0 : 1))
      .sort(
        (a, b) =>
          a.value - b.value || d3.ascending(a.data.length, b.data.length)
      );

    // Calculate and adjust height based on node count

    function getNodeCount(node) {
      if (!node.children || node.children.length === 0) {
        return 1;
      }
      if (!node || node.data.name === "" || node.data.name.includes("Inner")) {
        return 0 + node.children.reduce((acc, child) => acc + getNodeCount(child), 0);
      } else {
        return 1 + node.children.reduce((acc, child) => acc + getNodeCount(child), 0);
      }
    }

    const nodeCount = getNodeCount(root);
    // console.log('nodeCount', nodeCount);

    var dynamicWidth;
    if (nodeCount >= 100) {
      dynamicWidth = nodeCount * 15;
    } else if (nodeCount >= 50) {
      dynamicWidth = nodeCount * 18;
    } else if (nodeCount >= 30) {
      dynamicWidth = nodeCount * 20;
    } else if (nodeCount >= 20) {
      dynamicWidth = nodeCount * 25;
    } else if (nodeCount >= 10) {
      dynamicWidth = nodeCount * 35;
    } else {
      dynamicWidth = nodeCount * 45;
    }

    setWidth(Math.max(dynamicWidth, 300));

      // Compute the maximum cumulative length of any node in the tree.
      // function maxLength(d) {
      //   return d.data.length + (d.children ? d3.max(d.children, maxLength) : 0);
      // }
      function maxLength(node) {
        if (!node.children || node.children.length === 0) {
          return node.data.length || 0;
        }
        return node.data.length + d3.max(node.children, child => maxLength(child));
      }

      // Set the radius of each node by recursively summing and scaling the distance from the root.
      function setRadius(d, y0, k, multipleNodes) {
        if (k === Infinity) {
          k = 1;
        }
        if (multipleNodes) {
          d.radius = (y0 += d.data.length) * k;
          if (d.children) d.children.forEach((d) => setRadius(d, y0, k, true));
        } else {
          // there is only one node, remove stem
          d.radius = 0;
          if (d.children) d.children.forEach((d) => setRadius(d, y0, k, false));
        }
      }

      // link the tree up
      function linkStep(startAngle, startRadius, endAngle, endRadius) {
        const c0 = Math.cos((startAngle = ((startAngle - 90) / 180) * Math.PI));
        const s0 = Math.sin(startAngle);
        const c1 = Math.cos((endAngle = ((endAngle - 90) / 180) * Math.PI));
        const s1 = Math.sin(endAngle);
        return (
          "M" +
          startRadius * c0 +
          "," +
          startRadius * s0 +
          (endAngle === startAngle
            ? ""
            : "A" +
              startRadius +
              "," +
              startRadius +
              " 0 0 " +
              (endAngle > startAngle ? 1 : 0) +
              " " +
              startRadius * c1 +
              "," +
              startRadius * s1) +
          "L" +
          endRadius * c1 +
          "," +
          endRadius * s1
        );
      }

      function linkVariable(d) {
        return linkStep(
          d.source.x,
          d.source.radius,
          d.target.x,
          d.target.radius
        );
      }

      function linkConstant(d) {
        return linkStep(d.source.x, d.source.y, d.target.x, d.target.y);
      }

      function linkExtensionVariable(d) {
        return linkStep(d.target.x, d.target.radius, d.target.x, innerRadius);
      }

      function linkExtensionConstant(d) {
        return linkStep(d.target.x, d.target.y, d.target.x, innerRadius);
      }

      //functions for square tree
      function linkStepHorizontal(startY, startRadius, endY, endRadius) {
        const endYbigger = startY + (endY - startY) / 2;
        const startYbigger = startY - (endY - startY) / 2;

        const startRadiusNew = startRadius;
        const out =
          "M " +
          startRadius +
          " " +
          startY +
          " " +
          (endY === startY
            ? ""
            : "L " +
              startRadiusNew +
              " " +
              startY +
              " L " +
              startRadiusNew +
              " " +
              endY) +
          " L " +
          endRadius +
          " " +
          endY;
        return out;
      }

      function linkVariableHorizontal(d) {
        return linkStepHorizontal(
          d.source.x,
          d.source.radius,
          d.target.x,
          d.target.radius
        );
      }
      // end functions for square tree

      // Functions to highlight tree connection with index patient
      // Terribly complicated as can't directly use path elements (will result in extra lines)
      // Finds the lowest common ancestor of two nodes, then highlights the path between them except for the last connection (LCA - 1) which is drawn separately
      function findPathToRoot(node) {
        const path = [];
        let current = node;
        while (current.parent) {
          path.push(current);
          current = current.parent;
        }
        path.push(current);
        return path;
      }

      function findLCA(node1, node2) {
        const path1 = findPathToRoot(node1);
        const path2 = findPathToRoot(node2);
        const set1 = new Set(path1);
        for (let node of path2) {
          if (set1.has(node)) {
            return node;
          }
        }
        return null;
      }

      function findNodesPrecedingLCA(lca, node1, node2) {
        let node1Path = findPathToRoot(node1);
        let node2Path = findPathToRoot(node2);
        let child1 = lca.children.find(child => node1Path.includes(child));
        let child2 = lca.children.find(child => node2Path.includes(child));
        return [child1, child2];
      }

      function drawLCALines(node1, node2, root) {
        const lca = findLCA(node1, node2);
        let [child1, child2] = findNodesPrecedingLCA(lca, node1, node2);

        // console.log('child1', child1);
        // console.log('child2', child2);

        if (child1 && child2) {
          // Verticle line spanning between the two nodes before the LCA
          svg.append("line")
          .attr("class", "highlight-line")
          .attr("x1", root.radius)
          .attr("y1", child1.x)
          .attr("x2", root.radius)
          .attr("y2", child2.x)
          .attr("stroke", "crimson") 
          .attr("stroke-width", "3px")
          .attr("opacity", 0.9);

          //Horizontal lines from each node connecting to the vertical line above
          svg.append("line")
          .attr("class", "highlight-line")
          .attr("x1", root.radius)
          .attr("y1", child1.x)
          .attr("x2", child1.radius)
          .attr("y2", child1.x)
          .attr("stroke", "crimson")
          .attr("opacity", 0.9)
          .attr("stroke-width", "3px"); 

          svg.append("line")
          .attr("class", "highlight-line")
          .attr("x1", root.radius)
          .attr("y1", child2.x)
          .attr("x2", child2.radius)
          .attr("y2", child2.x)
          .attr("stroke", "crimson")
          .attr("opacity", 0.9)
          .attr("stroke-width", "3px");
        }
      }

      function highlightPath(d) {
        const primaryNode = root.descendants().find(n => n.data.name === primary);
        const pathToPrimary = findPathToRoot(primaryNode);
        const currentNodePath = findPathToRoot(d);
        const lca = findLCA(d, primaryNode);
      
        svg.selectAll('path')
          .style('stroke', p => {
            if (!p) {
              return 'silver'; 
            }
            if (currentNodePath.includes(p.target) && p.target.depth > lca.depth + 1) {
              return 'crimson';
            } else if (pathToPrimary.includes(p.target) && p.target.depth > lca.depth + 1) {
              return 'crimson';
            }
            return 'silver';
          });

          svg.selectAll('path')
          .style('stroke-width', p => {
            if (!p) {
              return '2px';
            }
            if (currentNodePath.includes(p.target) && p.target.depth > lca.depth + 1) {
              return '3px';
            } else if (pathToPrimary.includes(p.target) && p.target.depth > lca.depth + 1) {
              return '3px';
            }
            return '2px';
          });
      
        svg.selectAll('path')
          .style('opacity', p => {
            if (!p) {
              return 1;
            }
            if (currentNodePath.includes(p.target) && p.target.depth > lca.depth + 1) {
              return 0.7; // Highlight opacity
            } else if (pathToPrimary.includes(p.target) && p.target.depth > lca.depth + 1) {
              return 0.7; // Highlight opacity
            }
            return 1; // Default opacity
          });
      
        // Move highlighted segments to the front
        svg.selectAll('path')
          .filter(function () {
            return d3.select(this).style('stroke') === 'crimson';
          })
          .each(function () {
            this.parentNode.appendChild(this);
          });

          // Draw final lines manually
          drawLCALines(d, primaryNode, lca);
      }

      function removeHighlight() {
        svg.selectAll('path')
          .style('stroke', '#0d93e3')
          .style('stroke-width', '2px')
          .style('opacity', 1);
        svg.selectAll('.highlight-line').remove();
      }
  
      const cluster = d3
        .cluster()
        .size([360, innerRadius])
        .separation((a, b) => 1);

      const rectCluster = d3
        .cluster()
        .size([width, height])
        .separation((a, b) => 0.01);

      if (square) {
        rectCluster(root);
      } else {
        cluster(root);
      }

      setRadius(
        root,
        (root.data.length = 0),
        innerRadius / maxLength(root),
        root.descendants().length > 2 ? true : false
      );

      if (square) {
        //edges
        svg
          .append("g")
          .attr("fill", "none")
          .attr("stroke", "#0d93e3")
          .attr("stroke-width", "2px")
          .selectAll("path")
          //.data(root.links())
          .data(root.links().filter((link) => link.source.data.name !== "")) // this will remove the hanging line when the root is empty
          .join("path")
          .each(function (d) {
            d.target.linkNode = this;
          }) //this is used for the highlighted path
          .attr("d", linkVariableHorizontal);

        svg
          .append("g")
          .selectAll("circle")
          .data(root.leaves())
          .join("circle")
          .attr("dy", ".31em")
          .attr("fill", "rgba(0,0,0,0.55)")
          .attr("fill-opacity", 0.8)
          .attr("transform", (d) => `translate(${d.radius},${d.x})`) //time scaled tree
          .attr("r", 6)
          .style("fill", (d) => {
            if (d.data.name === primary) {
              return "darkred";
            }
            if (d.data.name.slice(-7) == "No data") {
              return "rgba(0,0,0,0.3)";
            }
            const nodeSeqId = getNodeSeqId(d);
            if (nodeSeqId && highlightedSeqIds.has(nodeSeqId)) {
              return "purple";
            }
          })
          .on("click", (e, d) => {
            const nodeMicroId = getNodeMicroId(d);
            if (nodeMicroId === null) {
              return;
            }
            //reset tree data
            dispatch(patientActions.storeTree({ tree: undefined }, {}));
            dispatch(patientActions.storeSnpDistance(5));
            //redict to new patient page
            redirect(nodeMicroId);
          })
          .on("mouseover", (e, d) => {
            highlightPath(d);
            const [mouse_x, mouse_y] = d3.pointer(e, svg.node());

            const nodeSeqId = getNodeSeqId(d);
            const collectDate = d.data.collectdt;
            if (nodeSeqId && collectDate) {
              const nodeName = d.data.name;
              const snpDistance = snpDistances[nodeSeqId];
              const firstPosDate = new Date(firstPos.split("T")[0]);
              const daysAgo = -Math.round((new Date(collectDate) - firstPosDate) / (1000 * 60 * 60 * 24));
              
              setTooltipId(nodeSeqId);
              setTooltipName(nodeName);
              setTooltipDate(`Collection date: ${collectDate}`);

              if (d.data.name !== primary) {
                // Not the primary node
                setTooltipSNPs(`${snpDistance} SNPs from ${primary}`);
                setTooltipDaysAgo(daysAgo > 0 ? `(${daysAgo} days ago)` : `(${-daysAgo} days later)`);
              } else {
                // Primary node
                setTooltipSNPs("");
                setTooltipDaysAgo('(Today)');
              }

              setTooltipPosition({
                x: mouse_x + tooltip_x_offset,
                y: mouse_y + tooltip_y_offset,
              });
              setShowTooltip(true);
            }
        })
        .on("mousemove", (e, d) => {
          const [mouse_x, mouse_y] = d3.pointer(e, svg.node());
          setTooltipPosition({
            x: mouse_x + tooltip_x_offset,
            y: mouse_y + tooltip_y_offset,
          });
        })
        .on("mouseleave", () => {
          removeHighlight();
          setShowTooltip(false);
        })
        .style("cursor", "pointer");

        const redirect = (micro_id) => {
          navigate(`/patient/${micro_id}`);
        };

        // tip labels
        svg
        .append("g")
        .selectAll("text")
        .data(root.leaves())
        .join("text")
        .attr("dy", ".31em")
        .attr("transform", (d) => `translate(${d.radius + tipLabelPadding},${d.x})`)
        .style("font-size", "14px")
        .style("cursor", "pointer")
        .each(function(d) {
          var snpDistanceText = "";
          const nodeSeqId = getNodeSeqId(d);
          if (
            d.data.name !== primary &&
            nodeSeqId &&
            snpDistances[nodeSeqId] !== undefined
          ) {
            snpDistanceText = snpDistances[nodeSeqId];
          } else {
            snpDistanceText = "";
          }
          
          // Append source name
          d3.select(this)
            .append("tspan")
            .text(d.data.name.replace(/_/g, " "))
            .style("fill", (d) => {
              if (d.data.name === primary) return "darkred";
              if (d.data.name.slice(-7) == "No data") return "rgba(0,0,0,0.3)";
              const nodeSeqId = getNodeSeqId(d);
              if (nodeSeqId && highlightedSeqIds.has(nodeSeqId)) {
                return "purple";
              }
              return "black"; // Default color
            });

            // Append SNP distance
            d3.select(this)
              .append("tspan")
              .attr("dy", "0")
              .attr("dx", "3")
              .text(snpDistanceText)
              .style("font-size", "10px")
              .style("fill", "darkorange");
          })
          .on("click", (e, d) => {
            const nodeMicroId = getNodeMicroId(d);
            if (nodeMicroId === null) {
              return;
            }
            redirect(nodeMicroId);
          })
          .on("mouseover", (e, d) => {
            highlightPath(d);
            const [mouse_x, mouse_y] = d3.pointer(e, svg.node());

            const nodeSeqId = getNodeSeqId(d);
            const collectDate = d.data.collectdt;
            if (nodeSeqId && collectDate) {
              const nodeName = d.data.name;
              const snpDistance = snpDistances[nodeSeqId];
              const firstPosDate = new Date(firstPos.split("T")[0]);
              const daysAgo = -Math.round((new Date(collectDate) - firstPosDate) / (1000 * 60 * 60 * 24));
              
              setTooltipId(nodeSeqId);
              setTooltipName(nodeName);
              setTooltipDate(`Collection date: ${collectDate}`);

              if (d.data.name !== primary) {
                // Not the primary node
                setTooltipSNPs(`${snpDistance} SNPs from ${primary}`);
                setTooltipDaysAgo(daysAgo > 0 ? `(${daysAgo} days ago)` : `(${-daysAgo} days later)`);
              } else {
                // Primary node
                setTooltipSNPs("");
                setTooltipDaysAgo('(Today)');
              }

              setTooltipPosition({
                x: mouse_x + tooltip_x_offset,
                y: mouse_y + tooltip_y_offset,
              });
              setShowTooltip(true);
            }
        })
        .on("mousemove", (e, d) => {
          const [mouse_x, mouse_y] = d3.pointer(e, svg.node());
          setTooltipPosition({
            x: mouse_x + tooltip_x_offset,
            y: mouse_y + tooltip_y_offset,
          });
        })
        .on("mouseleave", () => {
          removeHighlight();
          setShowTooltip(false);
        });
          
        let maxNodeLength = d3.max(root.descendants(), d => d.data.length);

        function calculateBarLength() {
          let min_dist = null;
          const links = root.links();
          for (let i = 0; i < links.length; i++) {
              let dist = links[i].target.radius - links[i].source.radius;
              if ((min_dist === null && dist !== 0) || (dist < min_dist && dist !== 0)) {
                  min_dist = dist;
              }
          }
          if (maxNodeLength === 0 || min_dist === null || links.length === 1) {
              min_dist = 0;
          }
          return min_dist;
      }
      
        // scale bar
        if (calculateBarLength() > 0) {
          svg
            .append("line")
            .attr("y1", width - 2)
            .attr("x1", 0)
            .attr("y2", width - 2)
            .attr("x2", calculateBarLength())
            .attr("fill", "none")
            .attr("stroke", "darkorange")
            .attr("stroke-width", "1px");

          // scale bar ticks
          svg.append("line") 
            .attr("y1", width - 5)
            .attr("x1", 0)
            .attr("y2", width + 1)
            .attr("x2", 0)
            .attr("stroke", "darkOrange")
            .attr("stroke-width", "1px");

          svg.append("line")
            .attr("y1", width - 5)
            .attr("x1", calculateBarLength())
            .attr("y2", width + 1)
            .attr("x2", calculateBarLength())
            .attr("stroke", "darkorange")
            .attr("stroke-width", "1px");

          // scale bar text
          svg
          .append("text")
          .text(() => {
            if (data.name === "" || maxNodeLength === 0) {
              return ""; // if maximum node length is 0 or if node connects to root, hide legend text
            }
            if (data.min_snp) {
              if (data.min_snp === 1) {
                return data.min_snp + " SNP";
              } else {
                return data.min_snp + " SNPs";
              }
            }
          })
          .style("font-size", "11px")
          .style("fill", "darkorange")
          .style("text-anchor", "middle")
          .attr("y", width + 12)
          .attr("dx", calculateBarLength() / 2);
        }
      } else {
        const linkExtension = svg
          .append("g")
          .attr("fill", "none")
          .attr("stroke", "rgba(0,0,0,0.48)")
          .attr("stroke-opacity", 0.25)
          .selectAll("path")
          .data(root.links().filter((d) => !d.target.children))
          .join("path")
          .each(function (d) {
            d.target.linkExtensionNode = this;
          })
          .attr("d", linkExtensionConstant);

        const link = svg
          .append("g")
          .attr("fill", "none")
          .attr("stroke", "#0d93e3")
          .attr("stroke-width", "2px")
          .selectAll("path")
          .data(root.links())
          .join("path")
          .each(function (d) {
            d.target.linkNode = this;
          })
          .attr("d", linkConstant)
          .attr("stroke", (d) => d.target.color);

        svg
          .append("g")
          .selectAll("text")
          .data(root.leaves())
          .join("text")
          .attr("dy", ".31em")
          .attr(
            "transform",
            (d) =>
              `rotate(${d.x - 90}) translate(${innerRadius + 4},0)${
                d.x < 180 ? "" : " rotate(180)"
              }`
          )
          .attr("text-anchor", (d) => (d.x < 180 ? "start" : "end"))
          .text((d) => d.data.name.replace(/_/g, " "));

        function update(checked) {
          const t = d3.transition().duration(0);
          linkExtension
            .transition(t)
            .attr("d", checked ? linkExtensionVariable : linkExtensionConstant);
          link.transition(t).attr("d", checked ? linkVariable : linkConstant);
        }

        update(1);
    }
  };

  return (
    <>
      <Container>
        <svg
          id="tree"
          ref={ref}
          style={{
            height: `${width}px`,
            width: `${height}px`,
            marginRight: "0px",
            marginLeft: "0px",
          }}
        ></svg>{" "}
        {showTooltip && (
            <div
                style={{
                    position: "absolute",
                    left: `${tooltipPosition.x}px`,
                    top: `${tooltipPosition.y}px`,
                    backgroundColor: "#132f52",
                    opacity: 0.9,
                    color: "white",
                    padding: "5px",
                    fontSize: "13px",
                    borderRadius: "5px",
                    pointerEvents: "none",
                    zIndex: 1000,
                }}
            >
                <div><b>{tooltipName}</b> ({tooltipId})</div>
                <div>{tooltipSNPs}</div>
                <div>{tooltipDate} <span style={{color: "yellow"}}>{tooltipDaysAgo}</span></div>
            </div>
        )}
      </Container>
    </>
  );
}
